<template>
  <template v-if="sortedShortcuts.length <= 3">
    <CButton
      v-for="shortcut in sortedShortcuts"
      :key="shortcut.id"
      v-tooltip="shortcut.tooltip || shortcut.displayName"
      :circle="isFullscreen && !pitcherSettings.alternative_canvas_fullscreen"
      class="w-11 h-11"
      secondary
      strong
      @click="onShortcutClick(shortcut)"
    >
      <template #icon>
        <CIcon :icon="shortcut.icon || 'browser'" size="18" />
      </template>
    </CButton>
  </template>
  <CPopover v-else v-model:show="isPopoverVisible" placement="bottom" trigger="click" :width="80">
    <template #trigger>
      <CButton
        :circle="isFullscreen && !pitcherSettings.alternative_canvas_fullscreen"
        class="w-11 h-11"
        secondary
        strong
        @click="isPopoverVisible = true"
      >
        <template #icon>
          <CIcon icon="ellipsis" size="18" />
        </template>
      </CButton>
    </template>
    <div class="flex flex-col gap-2 items-center max-h-[252px] overflow-y-auto overflow-x-hidden">
      <CButton
        v-for="shortcut in sortedShortcuts"
        :key="shortcut.id"
        v-tooltip="shortcut.tooltip || shortcut.displayName"
        class="w-11 h-11"
        secondary
        strong
        @click="onShortcutClick(shortcut)"
      >
        <template #icon>
          <CIcon :icon="shortcut.icon || 'browser'" size="18" />
        </template>
      </CButton>
    </div>
  </CPopover>
</template>

<script setup lang="ts">
import { computed, inject, ref, type UnwrapRef, type ComputedRef } from 'vue'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import { useInstalledEmbeddables } from '@canvas-builder/composables/useInstalledEmbeddables'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { navigateToEmbeddableForPages } from '@canvas-builder/App.helpers'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const { isEditMode, isFullscreen, mode, componentNodesById, sectionComponentNodesBySectionId, contentRef } = useCanvas()
const { canvasPages } = useCanvasPages()
const { shortcuts: allShortcuts } = useInstalledEmbeddables()

const isPopoverVisible = ref(false)

const currentModeShortcuts = computed(() => {
  if (mode.value === 'admin') return []
  else if (isFullscreen.value) {
    return allShortcuts.value.fullscreen
  } else if (isEditMode.value) {
    return allShortcuts.value.edit
  }
  return allShortcuts.value.presentation
})

const sortedShortcuts = computed(() => {
  return [...currentModeShortcuts.value].sort((a, b) => (a.order_override || 0) - (b.order_override || 0))
})

function onShortcutClick(shortcut: UnwrapRef<typeof currentModeShortcuts>[number]) {
  const appName = shortcut.appName
  if (!appName) return
  if ((pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') === 'collapsibles') {
    console.warn('navigateToEmbeddable is not supported in collapsibles mode.')
    return
  }

  navigateToEmbeddableForPages({
    appName,
    canvasPages: canvasPages.value,
    componentNodesById: componentNodesById.value,
    sectionComponentNodesBySectionId: sectionComponentNodesBySectionId.value,
    isEditMode: isEditMode.value,
    canvasContentElement: contentRef.value?.$el,
  })
  isPopoverVisible.value = false
}
</script>
